/**
 * 🗄️ useDatabases Hook
 * React Query hook for database operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { databaseService } from '../services/databaseService';
import type {
  DatabaseQuery,
  CreateDatabaseRequest,
  UpdateDatabaseRequest,
} from '@troia-v3/types';

/**
 * Hook to fetch all databases
 */
export const useDatabases = (query?: DatabaseQuery) => {
  return useQuery({
    queryKey: ['databases', query],
    queryFn: () => databaseService.getDatabases(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch single database by ID
 */
export const useDatabase = (id?: string) => {
  return useQuery({
    queryKey: ['databases', id],
    queryFn: () => databaseService.getDatabaseById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create database
 */
export const useCreateDatabase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDatabaseRequest) => databaseService.createDatabase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    },
  });
};

/**
 * Hook to update database
 */
export const useUpdateDatabase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDatabaseRequest }) =>
      databaseService.updateDatabase(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      queryClient.invalidateQueries({ queryKey: ['databases', variables.id] });
    },
  });
};

/**
 * Hook to delete database
 */
export const useDeleteDatabase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => databaseService.deleteDatabase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    },
  });
};
